/** @typedef {import('../types/USGSDeviceGraph_types').USGSDeviceGraphProps} USGSDeviceGraphProps */

import BaseChart from 'components/BaseChart/BaseChart';
import { setSelectedDeviceId } from 'features/integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/integrations/state/thunks';
import { useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { buildUSGSDeviceChartModel } from '../utils/usgsDeviceGraphUtils';
import * as usgsEventUtils from '../utils/usgsEventUtils';
import {
  buildAlignedDatasets,
  buildChartOptionFromModel,
  buildSeriesFromDatasets,
  buildUSGSTooltipFormatter,
  buildXAxisLabelFormatter,
  computeUnitDisplay,
  computeYAxisLabel,
} from '../utils/usgsGraphDisplayUtils';

/**
 * USGS device chart component. Renders a chart for USGS devices.
 *
 * Currently being used in Connected Devices Device Graph feature.
 *
 * Detailed props are documented for editor support below. The shapes are
 * also defined in `types/USGSDeviceGraph_types.ts` (imported via the typedef above).
 *
 * @param {Object} props
 * @param {string} [props.title] - Optional chart title.
 * @param {import('../types/USGSDeviceGraph_types').USGSDevice[]} [props.devices] - Array of USGS devices to display.
 * @param {Record<string, any>} [props.selectedDeviceMap] - Map of selected device ids to metadata.
 * @param {string} [props.selectedDataType] - Currently-selected series/data type key.
 * @param {boolean} [props.unitType] - Unit selection flag (e.g., metric vs imperial).
 * @param {(value: boolean | ((prev: boolean) => boolean)) => void} [props.setUnitType] - Setter for `unitType`.
 * @param {string|number|Date} [props.startTime] - Optional display range start.
 * @param {string|number|Date} [props.endTime] - Optional display range end.
 * @param {(index: number) => void} [props.onSelectedDeviceIndex] - Callback when a device index is selected.
 * @param {(v: boolean) => void} [props.setShowCurrentDevice] - Toggle to show the current device.
 * @param {(v: boolean) => void} [props.setShowProvisional] - Toggle to show provisional values.
 * @returns {JSX.Element | null}
 */
const USGSDeviceGraph = ({
  title,
  selectedDeviceMap,
  selectedDataType,
  unitType,
  setUnitType,
  startTime,
  endTime,
  onSelectedDeviceIndex,
  setShowCurrentDevice,
  setShowProvisional,
}) => {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery(
    startTime != null && endTime != null ? { startTime, endTime } : undefined,
  );
  const dispatch = useDispatch();
  const chartRef = useRef(null);

  const usgsModel = useMemo(
    () =>
      buildUSGSDeviceChartModel({
        devices: connectedDevices,
        selectedDeviceMap,
        selectedDataType,
        unitType,
        startTime,
        endTime,
      }),
    [connectedDevices, selectedDeviceMap, selectedDataType, unitType, startTime, endTime],
  );

  const navigate = useNavigate();
  const location = useLocation();

  const { labels, datasets } = usgsModel || { labels: [], datasets: [] };

  const hasUSGSData = Array.isArray(datasets) && datasets.length > 0;

  const alignedDatasets = useMemo(() => buildAlignedDatasets(datasets), [datasets]);

  const hasSelectedDevice = useMemo(
    () => Object.keys(selectedDeviceMap || {}).some((k) => selectedDeviceMap[k]),
    [selectedDeviceMap],
  );

  const usgsSeries = useMemo(() => {
    if (!hasUSGSData) return [];
    return buildSeriesFromDatasets(datasets, selectedDataType);
  }, [hasUSGSData, datasets, selectedDataType]);

  const tooltipFormatter = useMemo(() => {
    if (!hasUSGSData) return null;
    return buildUSGSTooltipFormatter(alignedDatasets, unitType);
  }, [hasUSGSData, alignedDatasets, unitType]);

  const xAxisLabelFormatter = useMemo(() => buildXAxisLabelFormatter(), []);

  const yAxisLabel = useMemo(
    () => computeYAxisLabel(datasets, selectedDataType, unitType),
    [datasets, selectedDataType, unitType],
  );

  const unitDisplay = useMemo(
    () => computeUnitDisplay(datasets, selectedDataType, unitType),
    [datasets, selectedDataType, unitType],
  );

  const usgsOption = useMemo(() => {
    if (!hasUSGSData) return null;
    return buildChartOptionFromModel({
      title,
      labels,
      startTime,
      endTime,
      series: usgsSeries,
      tooltipFormatter,
      xAxisLabelFormatter,
      yAxisLabel,
    });
  }, [
    hasUSGSData,
    labels,
    usgsSeries,
    tooltipFormatter,
    startTime,
    endTime,
    xAxisLabelFormatter,
    yAxisLabel,
  ]);

  const handleResetZoom = () => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;
    instance.dispatchAction({ type: 'restore' });
    instance.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
  };

  const handleToggleUnits = () => {
    if (typeof setUnitType === 'function') setUnitType((prev) => !prev);
  };

  const usgsOnEvents = useMemo(() => {
    return {
      click: (params) => {
        if (!params || params.componentType !== 'series') return;

        // Prevent the chart click from bubbling to parent elements
        // which may also handle clicks and open the wrong datasheet.
        try {
          // params.event?.stopPropagation?.();
          // params.event?.preventDefault?.();
        } catch (e) {}

        // Select device index by dataset meta id
        try {
          const seriesIndex = params.seriesIndex ?? 0;
          const datasetForSelection = alignedDatasets[seriesIndex] || datasets[seriesIndex] || {};
          const targetId = datasetForSelection?._meta?.id;
          if (targetId && Array.isArray(connectedDevices)) {
            const globalIndex = (connectedDevices || []).findIndex((d) => d.id === targetId);
            if (globalIndex >= 0) {
              if (typeof onSelectedDeviceIndex === 'function') onSelectedDeviceIndex(globalIndex);
              setShowCurrentDevice?.(true);
            }
          }
        } catch (e) {
          // ignore selection errors
        }

        const seriesIndex = params.seriesIndex ?? 0;
        const dataPointIndex = params.dataIndex;
        const dataset = alignedDatasets[seriesIndex] || datasets[seriesIndex] || {};
        const datasetMeta = dataset?._meta || {};
        const deviceId = datasetMeta?.id ?? null;

        let dataRowId = usgsEventUtils.resolveDataRowIdFromParams(params, datasetMeta);
        if (dataRowId == null) {
          const pointMetadata = Array.isArray(datasetMeta?.data)
            ? datasetMeta.data[dataPointIndex]
            : null;
          dataRowId = pointMetadata?.did ?? null;
        }

        if (deviceId) {
          try {
            usgsEventUtils.setUidDidInUrl(deviceId, dataRowId, navigate, location);
          } catch (e) {
            // ignore
          }
          // Sync map and graph to the clicked device
          dispatch(setSelectedDeviceId(deviceId));
          dispatch(setSelectedDevice({ id: deviceId }));
        }
      },
    };
  }, [
    alignedDatasets,
    datasets,
    connectedDevices,
    onSelectedDeviceIndex,
    setShowCurrentDevice,
    dispatch,
  ]);

  if (!hasUSGSData || !usgsOption) {
    return (
      <div className="bg-primary h-100 rounded-3 overflow-hidden d-flex justify-content-center align-items-center fs-4 text-white">
        {hasSelectedDevice ? 'No Data in time range' : 'No device selected'}
      </div>
    );
  }
  //Return the USGS Device Graph here
  return (
    <BaseChart
      ref={chartRef}
      option={usgsOption}
      onEvents={usgsOnEvents}
      notMerge={true}
      autoResize
      height="100%"
      width="100%"
      controlsLeft={
        <Button type="button" className="m-2" onClick={handleToggleUnits}>
          Units: {unitDisplay ?? (unitType ? 'Metric' : 'Imperial')}
        </Button>
      }
      controlsRight={
        <div className="d-flex align-items-center gap-1">
          <Button
            type="button"
            className="m-0 ms-1 p-2  d-flex align-items-center justify-content-center"
            onClick={() => setShowProvisional?.(true)}
            aria-label="Show USGS provisional disclaimer"
            title="USGS provisional disclaimer"
            style={{ minWidth: 44 }}
          >
            <IoMdInformationCircleOutline size={20} />
          </Button>
          <Button type="button" className="m-2 ms-1" onClick={handleResetZoom}>
            Reset Zoom
          </Button>
        </div>
      }
    />
  );
};

export default USGSDeviceGraph;
